import { createHtmlElementFromString, formatText } from '../../utils/html.utils';
import { FirstLevelMenuItem } from '../../api/menu-api';
import { ventilators } from '../../ventilators.module';
import { baseUrl } from '../../api/general-api';
import { PageModule } from '../../utils/page.module';
import { Param, writeToUrl } from '../../utils/url.utils';
import { getUrlForImage } from '../../utils/image.utils';
import { header } from '../header/header.module';
import { resourcesCenter } from './resources-center.module';
import { adaptOverviewPageForPartnernet } from '../../utils/helper.utils';

/**
 * Creates the overview for the e-catalog.
 */
export class ResourcesOverview extends PageModule {
  /**
   * Selector that is rendered in this view.
   */
  componentSelector = '.resources-overview';

  /**
   * Markup for the overview
   */
  template = `<div class="resources-overview">
  <section class="section section--stretch-content">
    <div class="section section__element" data-animate-when-visible="fade-in-up">
        <div class="text-and-image-component text-and-image-component--text-full">
            <div class="text-and-image-component__right-content">
                <h3 class="text-and-image-component__heading"></h3>
                <div class="text-and-image-component__full-image-right-content">
                    <p class="text-and-image-component__text text-large"></p>
                </div>
            </div>
        </div>
    </div>
    <div class="section section__element">
      <div class="tile__grid tile__grid--product-selector tile__grid--four-columns-desktop"></div>
    </div>
    <div class="section section__element" data-animate-when-visible="fade-in-up">
        <div class="text-and-image-component text-and-image-component--text-full">
            <div class="text-and-image-component__right-content">
                <h3 class="text-and-image-component__heading"></h3>
                <div class="text-and-image-component__full-image-right-content">
                    <p class="text-and-image-component__text text-large"></p>
                </div>
            </div>
        </div>
    </div>
    <div class="section section__element">
        <div class="tile__grid tile__grid tile__grid--four-columns-desktop"></div>
    </div>
  </section>
</div>`;

  /**
   * Markup for a teaser card.
   */
  teaserCardMarkup = `<a role="button" class="tile tile--teaser-card tile--isolated-image" data-animate-when-visible="fade-in-up">
    <div class="tile__image">
        <img src="" alt="" />
    </div>
    <div class="tile__content">
        <h4 class="tile__headline"></h4>
        <p class="tile__rich-text text-large"></p>
        <div class="button-section">
          <button class="button"></button>
        </div>
    </div>
  </a>`;

  /**
   * Markup for a product in the product selector.
   */
  productSelectorMarkup = `<a role="button" class="tile tile--product-selector tile--product-selector--reduced tile--isolated-image" data-animate-when-visible="fade-in-up">
    <div class="tile__image">
        <img src="" alt="" />
    </div>
    <div class="tile__content">
        <h4 class="tile__headline"></h4>
        <button class="button button--icon-only">
            <span class="button__icon-single">
                <svg width="22" height="20" viewBox="0 0 22 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16.4167 6.24756L20.5833 10.4142L16.4167 14.5809" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path>
                    <path d="M20.5833 10.4141H1.41667" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path>
                </svg>
            </span>
        </button>
        <svg width="22" height="20" viewBox="0 0 22 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16.4167 6.24756L20.5833 10.4142L16.4167 14.5809" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path>
            <path d="M20.5833 10.4141H1.41667" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path>
        </svg>
    </div>
  </a>`;

  /**
   * Setup the component with translations
   * @param translations The translations to initialize
   */
  constructor(translations: { [key: string]: string }) {
    super();
    this.translations = translations;
  }

  /**
   * Create the overview
   * @param parent The parent element of the overview (e-catalog).
   * @param menuItems The menu items that are shown in the teaser cards.
   */
  renderView(parent: HTMLElement, menuItems: FirstLevelMenuItem[]): void {
    // hide the sub nav
    header.showSubNav(false);

    // Hide the headers shadow before user scrolled
    header.setHideShadowBeforeScrolled(true);

    // set the page title
    document.title = this.translations.headerTitle;

    // Set labels
    this.renderTranslations();

    // Render teaser cards
    this.renderTeaserCards(parent, menuItems);

    // Render products
    this.renderProducts(parent);

    formatText(parent);
  }

  /**
   * Renders the translated text for the products and categories.
   */
  renderTranslations(): void {
    const fullTexts = this.pageComponent.querySelectorAll<HTMLElement>('.text-and-image-component--text-full');
    // First text above the products
    fullTexts[0].querySelector<HTMLElement>('.text-and-image-component__heading').innerHTML =
      this.translations.productsHeadline +
      (this.translations.productsHeadlineLight
        ? ` <span class="text-color-light">${this.translations.productsHeadlineLight}</span>`
        : '');
    fullTexts[0].querySelector<HTMLElement>('.text-and-image-component__text').innerText =
      this.translations.productsText;

    // Second text above the support
    fullTexts[1].querySelector<HTMLElement>('.text-and-image-component__heading').innerHTML =
      this.translations.categoriesHeadline +
      (this.translations.categoriesHeadlineLight
        ? ` <span class="text-color-light">${this.translations.categoriesHeadlineLight}</span>`
        : '');
    fullTexts[1].querySelector<HTMLElement>('.text-and-image-component__text').innerText =
      this.translations.categoriesText;
  }

  /**
   * Renders the teaser cards to the overview depending on the given menu items.
   * @param parent The parent element of the overview (e-catalog).
   * @param menuItems The menu items that are shown in the teaser cards.
   */
  renderTeaserCards(parent: HTMLElement, menuItems: FirstLevelMenuItem[]): void {
    const teaserCardContainer = parent.querySelector('.tile__grid:not(.tile__grid--product-selector)');
    menuItems.forEach((item) => {
      const teaserCard = createHtmlElementFromString(this.teaserCardMarkup) as HTMLAnchorElement;
      const img = teaserCard.querySelector<HTMLImageElement>('.tile__image img');
      if (item.image) {
        img.src = getUrlForImage(item.image?.renditions?.large?.link);
        img.alt = item.name;
      } else {
        img.parentElement.remove();
      }
      teaserCard.querySelector<HTMLElement>('.tile__headline').innerText = item.label;
      teaserCard.querySelector<HTMLElement>('.text-large').innerText = item.description;
      teaserCard.querySelector<HTMLElement>('.button').innerText = this.translations.viewProduct;
      teaserCard.addEventListener('click', () => {
        // update the header but don't reload, because the reload is triggered by URL change event already
        resourcesCenter.changeHeaderFromOverview(parent.querySelector('.section--header'), item.id);
      });
      teaserCardContainer.appendChild(teaserCard);
    });
  }

  /**
   * Fetches the products and renders it to the overview.
   * @param parent The parent element of the overview (resources center).
   */
  renderProducts(parent: HTMLElement): void {
    const productContainer = parent.querySelector('.tile__grid--product-selector');
    ventilators.getCachedVentilators(false, null, true).then((ventilators) => {
      ventilators.forEach((ventilator) => {
        const productTile = createHtmlElementFromString(this.productSelectorMarkup) as HTMLAnchorElement;
        const img = productTile.querySelector<HTMLImageElement>('.tile__image img');
        if (ventilator.image) {
          img.src = baseUrl + ventilator.image.renditions.small.link;
        }
        img.alt = ventilator.name;
        productTile.querySelector<HTMLElement>('.tile__headline').innerText = ventilator.name;
        productTile.addEventListener('click', () => {
          const overviewPage = adaptOverviewPageForPartnernet(ventilator.resourceArticleLink);
          writeToUrl([
            { param: Param.CUSTOM_URL, value: overviewPage },
            { param: Param.VENTILATOR, value: ventilator.ventilatorTaxonomy }
          ]);
          header.updateVentilatorDropdownSelection(ventilator.ventilatorTaxonomy);
          resourcesCenter.changeHeaderMenuSelection(parent.querySelector('.section--header'), null, null, true);
        });
        productContainer.appendChild(productTile);
      });

      formatText(parent);
    });
  }
}
